import React, { useState } from "react";
import NovelApprovedBookReaderV1 from "./NovelApprovedBookReaderV1";

// Presentation-only viewer. The caller supplies a blob URL only AFTER it has
// verified the exact PDF bytes and SHA-256 against the saved publication record.
// This component never fetches, generates, edits, approves, or locks a PDF.
export default function NovelExactPdfReviewPaneV1({
  pdfUrl = "",
  kind = "interior",
  isFR = false,
  approved = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const [displayMode, setDisplayMode] = useState("inspection");
  const label = (en, fr) => isFR ? fr : en;
  const isCover = kind === "cover";
  const canReadAsBook = approved && !isCover;
  const showReader = canReadAsBook && displayMode === "book";

  // Do not show any iframe for arbitrary external URLs or an unverified state.
  // The owner-only caller retains responsibility for artifact verification.
  if (typeof pdfUrl !== "string" || !pdfUrl.startsWith("blob:")) {
    return null;
  }

  return (
    <section data-novel-exact-pdf-review-pane="read-only"
      style={{ display: "grid", gap: 10, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <strong>{approved
            ? (isCover
                ? label("Approved Cover PDF", "PDF de couverture approuvé")
                : label("Approved Interior PDF", "PDF intérieur approuvé"))
            : (isCover
                ? label("Exact Cover PDF Preview", "Aperçu du PDF exact de couverture")
                : label("Exact Interior PDF Preview", "Aperçu du PDF intérieur exact"))}</strong>
          <p className="ns-card-meta" style={{ margin: "4px 0 0" }}>
            {approved
              ? label("Original approved file. Read-only inspection does not change its approval.",
                  "Fichier original approuvé. Sa consultation en lecture seule ne modifie pas son approbation.")
              : label("Verified file for your review. Opening the preview does not approve or publish it.",
                  "Fichier vérifié pour votre examen. L'aperçu ne l'approuve ni ne le publie.")}
          </p>
        </div>
        {!showReader ? <button type="button" className="ns-button ns-button-outline"
          data-novel-exact-pdf-size-toggle="true"
          aria-pressed={expanded}
          onClick={() => setExpanded(value => !value)}>
          {expanded
            ? label("Standard Preview", "Aperçu normal")
            : label("Larger Preview", "Agrandir l'aperçu")}
        </button> : null}
      </div>
      {canReadAsBook ? <div role="group"
        aria-label={label("Approved interior reading mode", "Mode de lecture de l'intérieur approuvé")}
        style={{display:"flex",gap:9,flexWrap:"wrap"}}>
        <button type="button" className={showReader ? "ns-button ns-button-outline" : "ns-button ns-button-primary"}
          data-novel-approved-inspection="true" aria-pressed={!showReader}
          onClick={()=>setDisplayMode("inspection")}>
          {label("PDF Inspection", "Inspection PDF")}
        </button>
        <button type="button" className={showReader ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
          data-novel-approved-read-as-book="true" aria-pressed={showReader}
          onClick={()=>setDisplayMode("book")}>
          {label("Read as a Book", "Lire comme un livre")}
        </button>
      </div> : null}
      {showReader ? <NovelApprovedBookReaderV1 pdfUrl={pdfUrl} isFR={isFR} /> : <iframe
        title={approved
          ? (isCover
              ? label("Approved cover PDF for inspection", "PDF de couverture approuvé à consulter")
              : label("Approved interior PDF for inspection", "PDF intérieur approuvé à consulter"))
          : (isCover
              ? label("Exact cover PDF for creator review", "PDF exact de couverture à vérifier")
              : label("Exact interior PDF for creator review", "PDF intérieur exact à vérifier"))}
        src={pdfUrl}
        data-novel-exact-pdf-frame={expanded ? "expanded" : "standard"}
        style={{
          width: "100%",
          maxWidth: "100%",
          minHeight: expanded ? 670 : 540,
          height: expanded ? "min(86vh, 980px)" : "min(65vh, 720px)",
          border: "1px solid #bdd4c6",
          borderRadius: 10,
          background: "#fff",
        }}
      />}
      <p className="ns-card-meta" style={{ margin: 0 }}>
        {showReader
          ? label("Book view turns exact PDF pages horizontally, without changing the approved file.",
              "Le mode livre tourne les pages du PDF exact horizontalement, sans modifier le fichier approuvé.")
          : approved
          ? label("Use your browser's PDF controls to inspect the locked original. Corrections require a separate revision; this approved file remains unchanged.",
              "Utilisez les commandes PDF de votre navigateur pour consulter l’original verrouillé. Les corrections nécessitent une révision distincte ; ce fichier approuvé reste inchangé.")
          : label("Use your browser's PDF controls to inspect every page. This is the exact prepared file, not a provisional layout. Approval is a separate decision.",
              "Utilisez les commandes PDF de votre navigateur pour vérifier chaque page. Il s'agit du fichier exact préparé, et non d'une mise en page provisoire. L'approbation est une décision distincte.")}
      </p>
    </section>
  );
}
